<template>
  <a :href="video.link" class="profile-card text-decoration-none">
    <div
      class="video-card"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @click="onCardClick"
    >
      <div class="thumbnail-wrapper">
        <img
          :src="video.thumbnail"
          class="thumbnail"
          :class="{ active: !isPreviewing, blurred: video.locked }"
        />

        <video
          ref="previewVideo"
          :src="video.file_path"
          muted
          playsinline
          loop
          preload="metadata"
          class="preview-video"
          :class="{ active: isPreviewing && !video.locked }"
        ></video>

        <div class="play-overlay" v-show="!isPreviewing">
          <span class="play-btn">
            <i class="fa fa-play"></i>
          </span>
        </div>

        <div class="duration">{{ video.duration }}</div>

        <button v-if="video.locked" class="subscribe-btn">
          Subscribe to watch
        </button>
      </div>

      <div class="video-info">
        <h4 class="title">{{ toSentenceCase(truncateWords(video.title, 3)) }}</h4>
        <div class="meta">
          <span class="chat-icon">
            <img :src="chat" class="phone-location-ico" />
          </span>
          <span class="view-icon">
            <img :src="view" class="phone-location-ico" />
            {{ video.likes }}
          </span>
        </div>
      </div>
    </div>
  </a>
</template>

<script>
import view from "@/assets/carbon_view-filled.png";
import chat from "@/assets/mynaui_chat-dots-solid.png";

export default {
  name: "VideoCard",
  props: {
    video: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      view,
      chat,
      isPreviewing: false,
      previewTimer: null,
      previewInterval: null,
      previewRequestId: 0,
      supportsHover: false
    };
  },

  mounted() {
    this.startPreview();
    this.previewInterval = window.setInterval(() => {
      this.startPreview();
    }, 5000);

    if (window && window.matchMedia) {
      this.supportsHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    }
  },

  beforeDestroy() {
    if (this.previewInterval) {
      clearInterval(this.previewInterval);
      this.previewInterval = null;
    }

    this.stopPreview();
  },

  methods: {
    toSentenceCase(text) {
      if (!text) return "";
      return text
        .toLowerCase()
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    truncateWords(text, wordLimit) {
      if (!text) return "";
      const words = text.split(" ");
      if (words.length <= wordLimit) return text;
      return words.slice(0, wordLimit).join(" ") + "…";
    },
    onMouseEnter() {
      if (!this.supportsHover) return;
      this.startPreview();
    },
    onMouseLeave() {
      if (!this.supportsHover) return;
      this.stopPreview();
    },
    onCardClick(event) {
      if (this.video.locked || this.supportsHover) return;

      if (!this.isPreviewing) {
        event.preventDefault();
        this.startPreview();
      }
    },
    startPreview() {
      if (this.video.locked) return;

      this.stopPreview();
      const requestId = ++this.previewRequestId;
      this.isPreviewing = true;

      this.$nextTick(() => {
        if (requestId !== this.previewRequestId || !this.isPreviewing) return;

        const video = this.$refs.previewVideo;
        if (!video) return;

        video.currentTime = 0;
        const playResult = video.play();
        if (playResult && typeof playResult.catch === "function") {
          playResult.catch(() => {});
        }

        this.previewTimer = window.setTimeout(() => {
          this.stopPreview();
        }, 5000);
      });
    },
    stopPreview() {
      this.previewRequestId += 1;

      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }

      this.isPreviewing = false;

      if (this.$refs.previewVideo) {
        this.$refs.previewVideo.pause();
        this.$refs.previewVideo.currentTime = 0;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.profile-card {
  flex: 0 0 auto;
  width: 210px !important;
  scroll-snap-align: start;
}

.video-card {
  background: #0f0f1a;
  border-radius: 0;
  overflow: hidden;
  color: #fff;
  width: 199px;
}

.thumbnail-wrapper {
  position: relative;
  height: 250px;
  overflow: hidden;
}

.thumbnail,
.preview-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0;
  opacity: 0;
  transition: opacity 0.25s ease, filter 0.25s ease;
  pointer-events: none;
}

.thumbnail.active,
.preview-video.active {
  opacity: 1;
}

.thumbnail.blurred {
  filter: blur(8px);
  transform: scale(1.05);
}

.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
}

.play-btn {
  width: 45px;
  height: 45px;
  background: #fff;
  color: #000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  padding: 2px 6px;
  font-size: 12px;
  border-radius: 4px;
  z-index: 2;
}

.subscribe-btn {
  position: absolute;
  bottom: 45px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4f8b;
  border: none;
  color: #fff;
  padding: 8px 4px;
  border-radius: 6px;
  font-size: 13px;
  width: 120px;
  z-index: 2;
}

.video-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;

  .title {
    font-size: 14px !important;
    margin: 0 0 6px;
  }
}

.meta {
  display: flex;
  gap: 10px;
  font-size: 12px;
  opacity: 0.7;
}

.chat-icon img,
.view-icon img {
  width: 16px;
  height: 16px;
}
</style>
